/**
 * DeviceService
 * Service for push notification device operations
 * Route: api/device
 */
import { BaseService } from '../base/BaseService';
/**
 * DeviceService
 * Handles push notification device registration and management
 */
export class DeviceService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? DeviceService.DEFAULT_SERVICE_PATH,
            basePath: DeviceService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== READ OPERATIONS ====================
    /**
     * Get all devices
     * GET api/device
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all devices with full response
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    /**
     * Get active devices
     * GET api/device/active
     */
    async getActive() {
        const response = await this.getActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get active devices with full response
     */
    getActiveRaw() {
        const endpoint = this.getEndpoint('active', 'active');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get device by ID
     * GET api/device/{id}
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Device ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get device by ID with full response
     */
    getByIdRaw(id) {
        const url = this.buildUrl(id);
        return this.http.get(url);
    }
    /**
     * Get device by token
     * GET api/device/by-token/{token}
     */
    async getByToken(token) {
        const response = await this.getByTokenRaw(token);
        if (!response.data) {
            throw new Error(`Device with token not found`);
        }
        return response.data;
    }
    /**
     * Get device by token with full response
     */
    getByTokenRaw(token) {
        const endpoint = this.getEndpoint('by-token', `by-token/${token}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== WRITE OPERATIONS ====================
    /**
     * Register a new device
     * POST api/device
     */
    async register(device) {
        const response = await this.registerRaw(device);
        if (!response.data) {
            throw new Error(response.message || 'Failed to register device');
        }
        return response.data;
    }
    /**
     * Register device with full response
     */
    registerRaw(device) {
        const url = this.buildUrl();
        return this.http.post(url, device);
    }
    /**
     * Update an existing device
     * PUT api/device/{id}
     */
    async update(id, device) {
        const response = await this.updateRaw(id, device);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update device');
        }
        return response.data;
    }
    /**
     * Update device with full response
     */
    updateRaw(id, device) {
        const url = this.buildUrl(id);
        return this.http.put(url, device);
    }
    /**
     * Delete a device
     * DELETE api/device/{id}
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete device with full response
     */
    deleteRaw(id) {
        const url = this.buildUrl(id);
        return this.http.delete(url);
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate device cache
     * POST api/device/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
DeviceService.DEFAULT_SERVICE_PATH = 'communications';
DeviceService.BASE_PATH = 'api/device';
//# sourceMappingURL=DeviceService.js.map